import { useState } from "react";
import Seo from "@/components/base/Seo";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/base/BackToTop";
import Breadcrumb from "@/components/base/Breadcrumb";
import { settingEntries, settingCategories } from "@/mocks/settings";

const collectionLd = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name: "洞洞杂货铺漫画设定百科",
  description: "洞洞杂货铺漫画设定百科：店铺设定、洞洞法则、隐藏线索等完整辞条，随查随用。",
};

const breadcrumbLd = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "首页", item: "https://comworld1.github.io/" },
    { "@type": "ListItem", position: 2, name: "设定百科", item: "https://comworld1.github.io/settings" },
  ],
};

export default function Settings() {
  const [active, setActive] = useState<string>("全部");
  const categories = ["全部", ...settingCategories];
  const list =
    active === "全部"
      ? settingEntries
      : settingEntries.filter((e) => e.category === active);

  return (
    <>
      <Seo
        title="洞洞杂货铺漫画设定百科 - 店铺设定、洞洞法则与隐藏线索"
        description="洞洞杂货铺漫画设定百科：店铺设定、洞洞法则、隐藏线索等设定辞条随查随用，帮你快速理解洞洞杂货铺故事背景与世界观细节。"
        keywords="洞洞杂货铺设定,洞洞法则,洞洞杂货铺隐藏线索,洞洞杂货铺店铺设定"
        canonicalPath="/settings"
        jsonLd={[collectionLd, breadcrumbLd]}
      />
      <Navbar />
      <main>
        <div className="mx-auto w-full max-w-6xl px-4 pt-8 md:px-6">
          <Breadcrumb items={[{ label: "设定百科" }]} />
        </div>

        <div className="mx-auto w-full max-w-6xl px-4 py-8 md:px-6 md:py-10">
          <div className="max-w-3xl">
            <span className="inline-flex rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900 whitespace-nowrap">
              考据工具
            </span>
            <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
              洞洞杂货铺设定百科
            </h1>
            <p className="mt-4 text-sm leading-7 text-foreground-700 md:text-base">
              把散落在漫画各处的设定与隐藏线索，按条目整理成一份可以随手查阅的百科。
              无论你是刚入坑，还是正在做深度考据，都能在这里找到需要的那条设定。
            </p>
          </div>

          <div className="mt-8 flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setActive(cat)}
                className={`rounded-full px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                  active === cat
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200/70"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
            {list.map((entry) => (
              <article
                key={entry.id}
                className="flex flex-col overflow-hidden rounded-2xl bg-background-100/70 ring-1 ring-background-200/70 transition-transform duration-300 hover:-translate-y-1"
              >
                <div className="relative h-44 w-full overflow-hidden">
                  <img
                    src={entry.image}
                    alt={`${entry.title}设定插画`}
                    className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 rounded-full bg-accent-500 px-2.5 py-1 text-xs font-medium text-background-50">
                    {entry.category}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <h2 className="font-heading text-lg font-bold text-foreground-950">
                    {entry.title}
                  </h2>
                  <p className="mt-2 text-sm leading-6 text-foreground-700">{entry.summary}</p>
                  <ul className="mt-4 space-y-2 border-t border-background-200 pt-4">
                    {entry.points.map((point, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2 text-sm leading-6 text-foreground-700"
                      >
                        <i className="ri-checkbox-circle-line mt-1 text-primary-600"></i>
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            ))}
          </div>
        </div>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}